import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, HelpCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: ''
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/faqs`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error('Error fetching faqs:', error);
    }
  };

  const handleSubmit = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      if (editingFaq) {
        await axios.put(`${BACKEND_URL}/api/admin/faqs/${editingFaq.id}`, formData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('FAQ mise à jour!');
      } else {
        const newFaq = { ...formData, id: `faq-${Date.now()}` };
        await axios.post(`${BACKEND_URL}/api/admin/faqs`, newFaq, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('FAQ ajoutée!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchFaqs();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette FAQ?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/faqs/${faqId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('FAQ supprimée!');
      fetchFaqs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (faq) => {
    setEditingFaq(faq);
    setFormData(faq);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: ''
    });
  };

  return (
    <div data-testid="admin-faq-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">FAQ</h1>
          <p className="text-sm text-[#B9B2A8]">Gérez les questions fréquentes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0C] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-[#F5F0E8] font-display">
                {editingFaq ? 'Modifier la FAQ' : 'Nouvelle FAQ'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Question</label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="Que faire si je suis témoin d'un harcèlement ?"
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Réponse</label>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  rows={4}
                  className="bg-white/5 border-white/10 text-[#F5F0E8] resize-none"
                />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                {editingFaq ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <motion.div
            key={faq.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white/5 border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-lg bg-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-4 h-4 text-[#C9A84C]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[#F5F0E8] mb-2">{faq.question}</h3>
                <p className="text-sm text-[#B9B2A8]">{faq.answer}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEditDialog(faq)} className="h-8 w-8 text-[#B9B2A8] hover:text-[#F5F0E8]">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(faq.id)} className="h-8 w-8 text-[#B9B2A8] hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
        {faqs.length === 0 && (
          <div className="text-center py-12 text-[#B9B2A8]">
            Aucune FAQ pour le moment. Cliquez sur "Ajouter" pour en créer une.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFAQPage;
